import React from 'react'
import { Empty, Card } from 'antd'
import { ExperimentOutlined } from '@ant-design/icons'
import { Vertical, Horizontal, MaterialIcon, Texto, BBDTag, addCommasToNumber } from '@gravitate-js/excalibrr'
import { useFormatter } from '@api/useFormatter'

// Use the API's SRWarning type structure
interface SRWarning {
  type: string
  threshold?: number | null
  variance?: number | null
}

interface MatchWarningsContainerProps {
  warnings?: SRWarning[]
  modelConfig?: unknown
}

export function MatchWarningsContainer({ warnings, modelConfig }: MatchWarningsContainerProps) {
  const { formatCurrency } = useFormatter()
  const currencySymbol = formatCurrency(null, true)

  // In details view, show all warnings including delivery reconciliation warnings
  // (unlike main grid where both movement_validation and delivery_reconciliation are filtered out)
  const filteredWarnings = warnings

  if (!filteredWarnings || filteredWarnings.length === 0) {
    return (
      <Vertical className='p-4' verticalCenter horizontalCenter flex={1}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={<Texto appearance='hint'>No warnings found for this match</Texto>}
        />
      </Vertical>
    )
  }

  function formatWarningType(type: string): string {
    return type
      .replace(/_mismatch$/, '')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (l) => l.toUpperCase())
  }

  function getWarningMessage(type: string): string {
    switch (type) {
      case 'supplier_mismatch':
        return 'Supplier does not match between invoices and BOLs'
      case 'terminal_mismatch':
        return 'Terminal does not match between invoices and BOLs'
      case 'gross_volume_mismatch':
        return 'Gross volume variance detected'
      case 'net_volume_mismatch':
        return 'Net volume variance detected'
      case 'total_amount_mismatch':
        return 'Total amount variance detected'
      case 'total_per_volume_price_mismatch':
        return 'Per unit price variance detected'
      case 'total_product_cost_mismatch':
        return 'Product cost variance detected'
      case 'total_tax_mismatch':
        return 'Tax total variance detected'
      case 'delivery_reconciliation':
        return 'Delivery reconciliation errors must be resolved'
      case 'movement_validation':
        return 'Order movement validation issues detected'
      default:
        return 'Variance detected'
    }
  }

  function formatValueWithUnit(value: number, type: string): string {
    // Handle percentage-based warning types first
    if (
      type === 'total_per_volume_price_mismatch' ||
      type === 'gross_volume_mismatch' ||
      type === 'net_volume_mismatch' ||
      type === 'total_product_cost_mismatch' ||
      type === 'total_tax_mismatch'
    ) {
      // Convert decimal to percentage display (0.04 -> 4.00%) to match config page
      return `${addCommasToNumber(value * 100, 2)}%`
    }
    if (type.includes('amount') || type.includes('cost') || type.includes('tax')) {
      return `${currencySymbol}${addCommasToNumber(value, 2)}`
    }
    if (type.includes('volume')) {
      return `${addCommasToNumber(value, 2)} gal`
    }
    if (type.includes('price')) {
      return `${currencySymbol}${addCommasToNumber(value, 2)}/gal`
    }
    return addCommasToNumber(value, 2)
  }

  function getWarningIcon(type: string) {
    switch (type) {
      case 'gross_volume_mismatch':
      case 'net_volume_mismatch':
        return <ExperimentOutlined style={{ fontSize: 20, color: 'var(--theme-warning)' }} />
      case 'total_amount_mismatch':
      case 'total_product_cost_mismatch':
        return <MaterialIcon icon='attach_money' size={20} style={{ color: 'var(--theme-warning)' }} />
      case 'total_per_volume_price_mismatch':
        return <MaterialIcon icon='analytics' size={20} style={{ color: 'var(--theme-warning)' }} />
      case 'total_tax_mismatch':
        return <MaterialIcon icon='receipt' size={20} style={{ color: 'var(--theme-warning)' }} />
      case 'terminal_mismatch':
        return <MaterialIcon icon='location_on' size={20} style={{ color: 'var(--theme-warning)' }} />
      case 'supplier_mismatch':
        return <MaterialIcon icon='location_on' size={20} style={{ color: 'var(--theme-warning)' }} />
      case 'delivery_reconciliation':
        return <MaterialIcon icon='local_shipping' size={20} style={{ color: 'var(--theme-warning)' }} />
      case 'movement_validation':
        return <MaterialIcon icon='route' size={20} style={{ color: 'var(--theme-warning)' }} />
      default:
        return <MaterialIcon icon='warning' size={20} style={{ color: 'var(--theme-warning)' }} />
    }
  }

  return (
    <Vertical className='p-4' gap={12} style={{ height: '100%', overflow: 'auto' }}>
      {filteredWarnings.map((warning, index) => {
        return (
          <Card key={`${warning.type}_${index}`} size='small' style={{ borderColor: 'var(--theme-warning)' }}>
            <Vertical gap={8}>
              <Vertical gap={4}>
                <Horizontal gap={8} verticalCenter>
                  {getWarningIcon(warning.type)}
                  <Texto weight='bold' category='p2'>
                    {formatWarningType(warning.type)}
                  </Texto>
                </Horizontal>
                <Texto category='p2' appearance='hint'>
                  {getWarningMessage(warning.type)}
                </Texto>
              </Vertical>

              {warning.variance !== null && warning.variance !== undefined && (
                <Horizontal gap={16}>
                  {warning.threshold !== null && warning.threshold !== undefined && (
                    <Vertical gap={2}>
                      <Texto appearance='hint' size='small'>
                        Threshold
                      </Texto>
                      <Texto weight='bold' size='small'>
                        {formatValueWithUnit(warning.threshold, warning.type)}
                      </Texto>
                    </Vertical>
                  )}
                  <Vertical gap={2}>
                    <Texto appearance='hint' size='small'>
                      Variance
                    </Texto>
                    <BBDTag warning>{formatValueWithUnit(warning.variance, warning.type)}</BBDTag>
                  </Vertical>
                </Horizontal>
              )}
            </Vertical>
          </Card>
        )
      })}
    </Vertical>
  )
}
